
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useNursingHome } from "@/hooks/use-nursing-home";
import { createNursingHomeRedirectUrl } from "@/lib/redirects";
import { SEOHead } from "@/components/seo/SEOHead";

/**
 * This page handles redirects from old nursing home URLs like /ireland/{nursing-home-slug}
 * It looks up the nursing home by slug, then redirects to the new URL structure
 */
export default function NursingHomeLookupPage() {
  const { nhSlug } = useParams<{nhSlug: string}>();
  const navigate = useNavigate();
  const { data: nursingHome, isLoading, error } = useNursingHome(nhSlug);
  const [redirectAttempted, setRedirectAttempted] = useState(false);

  useEffect(() => {
    if (nursingHome && !redirectAttempted && nursingHome.county_slug) {
      // We have the nursing home data, redirect to the proper page
      const redirectUrl = createNursingHomeRedirectUrl(nhSlug || "", nursingHome);
      setRedirectAttempted(true);
      navigate(redirectUrl, { replace: true });
    }
  }, [nursingHome, nhSlug, navigate, redirectAttempted]);

  // Track 404s for missing nursing homes
  useEffect(() => {
    if (error && !redirectAttempted && nhSlug) {
      console.error(`Failed to find nursing home for redirect: ${nhSlug}`, error);
      
      if (window.gtag) {
        window.gtag('event', 'missing_redirect', {
          event_category: 'Error',
          event_label: `Nursing home not found: ${nhSlug}`,
        });
      }
      
      // Redirect to NotFound page after logging
      setRedirectAttempted(true);
      navigate("/not-found", { replace: true });
    }
  }, [error, nhSlug, navigate, redirectAttempted]);

  return (
    <Layout>
      <SEOHead 
        title="Finding Your Nursing Home | Redirecting..."
        description="Please wait while we redirect you to the nursing home you're looking for."
      />
      <div className="container py-20 min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-grandpal-green mx-auto"></div>
          <p className="mt-4 text-lg">Looking up nursing home information...</p>
          <p className="mt-2 text-sm text-gray-500">You'll be redirected in a moment.</p>
        </div>
      </div>
    </Layout>
  );
}
